import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import api from 'services/api';
import useEscapeClose from 'hooks/useEscapeClose';
import IAAnalysisPanel from 'components/shared/IAAnalysisPanel';
import { aujourdHui } from '../../../../utils/jour';

const API_BASE = '';
const OFFRE_COLORS = { Essentielle: '#888', Professionnelle: '#1565c0', Premium: '#C8A84E' };
const SALAIRE = 75000;
const TSV_MENSUEL = 150000;
const QR_UNIT = 5000;
const OFFRE_TARIFS = { Essentielle: 130000, Professionnelle: 160000, Premium: 200000 };

const DEVIS_CATALOG = {
  video: {
    label: 'B — Videosurveillance',
    items: [
      { key: 'cam_int', label: 'Cameras interieures', prix: 85000, qty: 4 },
      { key: 'cam_ext', label: 'Cameras exterieures', prix: 120000, qty: 2 },
      { key: 'nvr', label: 'NVR/DVR enregistreur', prix: 250000, qty: 1 },
      { key: 'install_video', label: 'Installation (forfait)', prix: 150000, qty: 1 },
      { key: 'maint_video', label: 'Maintenance annuelle', prix: 180000, qty: 1 },
    ],
  },
  alarmes: {
    label: 'C — Alarmes & Detection',
    items: [
      { key: 'centrale', label: "Centrale d'alarme", prix: 350000, qty: 1 },
      { key: 'detect_mvt', label: 'Detecteurs de mouvement', prix: 45000, qty: 4 },
      { key: 'sirene', label: 'Sirenes int./ext.', prix: 35000, qty: 2 },
      { key: 'detect_ouv', label: 'Detect. ouverture portes/fenetres', prix: 25000, qty: 4 },
      { key: 'install_alarme', label: 'Installation (forfait)', prix: 120000, qty: 1 },
    ],
  },
  tsv: {
    label: 'D — Telesurveillance',
    mensuel: true,
    items: [
      { key: 'abo_tsv', label: 'Abonnement mensuel', prix: 150000, qty: 1 },
      { key: 'levee_doute', label: 'Levee de doute agent', prix: 75000, qty: 1 },
      { key: 'intervention', label: 'Intervention sur alarme', prix: 50000, qty: 1 },
    ],
  },
};

function recalcCout(j, n, rondes, tsv, qr) {
  const totalAgents = (j + n) * 2;
  return totalAgents * SALAIRE + (tsv ? TSV_MENSUEL : 0) + qr * QR_UNIT;
}

function recalcScore(baseScore, j, n) {
  // Score dynamique : chaque agent couvre ~8% de la vulnerabilite
  const totalAgents = (j + n) * 2;
  const coverage = Math.min(1, totalAgents * 0.08);
  const vuln = baseScore || 0;
  return Math.round(Math.max(15, 100 - vuln * (1 - coverage) - vuln * 0.05));
}

function recalcTarifs(j, n, tarifs) {
  const totalAgents = (j + n) * 2;
  const updated = {};
  for (const [nom, data] of Object.entries(tarifs)) {
    updated[nom] = { ...data, cout_mensuel: (data.tarif || 0) * totalAgents };
  }
  return updated;
}

// ── Inline Editable Value ──
function EditableVal({ value, onChange, suffix, color }) {
  const [editing, setEditing] = useState(false);
  const [tmp, setTmp] = useState(value);

  const commit = () => {
    const v = parseInt(tmp);
    if (!isNaN(v) && v >= 0) onChange(v);
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        type="number" min="0" autoFocus value={tmp}
        onChange={e => setTmp(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
        style={{ width: 44, fontSize: 11, fontWeight: 400, color: color || '#1A1A2E', border: '1px solid #8B1A1A', borderRadius: 4, padding: '2px 4px', textAlign: 'center' }}
      />
    );
  }
  return (
    <span
      onClick={() => { setTmp(value); setEditing(true); }}
      title="Cliquer pour modifier"
      style={{ cursor: 'pointer', fontWeight: 400, color: color || '#1A1A2E', borderBottom: '1px dashed #ccc', paddingBottom: 1 }}
    >
      {value}{suffix || ''}
    </span>
  );
}

// ── Scenario Card ──
function ScenarioCard({ title, color, bg, data, prefix, recommended, warning, zones, onFieldChange, auditScore }) {
  const j = data[`${prefix}_agents_jour`] || 0;
  const n = data[`${prefix}_agents_nuit`] || 0;
  const rondes = data[`${prefix}_rondes`] || 0;
  const tsv = data[`${prefix}_telesurveillance`];
  const qr = data[`${prefix}_points_qr`] || 0;
  const score = data[`${prefix}_score_securite`] || 0;
  const cout = Number(data[`${prefix}_cout_mensuel`]) || 0;
  const equip = (() => { try { return JSON.parse(data[`${prefix}_equipements`]); } catch { return []; } })();
  const tarifs = (() => { try { return typeof data[`${prefix}_tarifs`] === 'string' ? JSON.parse(data[`${prefix}_tarifs`]) : (data[`${prefix}_tarifs`] || {}); } catch { return {}; } })();
  const hasTarifs = Object.keys(tarifs).length > 0;

  const handleChange = (field, val) => {
    const newJ = field === 'agents_jour' ? val : j;
    const newN = field === 'agents_nuit' ? val : n;
    const newR = field === 'rondes' ? val : rondes;
    const newQR = field === 'points_qr' ? val : qr;
    const newCout = recalcCout(newJ, newN, newR, tsv, newQR);
    const newScore = recalcScore(auditScore, newJ, newN);
    const newTarifs = recalcTarifs(newJ, newN, tarifs);

    const updates = {
      [`${prefix}_${field}`]: val,
      [`${prefix}_cout_mensuel`]: newCout,
      [`${prefix}_score_securite`]: newScore,
      [`${prefix}_tarifs`]: JSON.stringify(newTarifs),
    };
    onFieldChange(updates);
  };

  return (
    <div style={{
      flex: 1, minWidth: 0, padding: 12, borderRadius: 10, background: bg, border: `1px solid ${color}`,
      position: 'relative', overflow: 'hidden',
    }}>
      {recommended && <div style={{ position: 'absolute', top: 0, right: 0, background: color, color: '#fff', padding: '2px 10px', borderRadius: '0 8px 0 8px', fontSize: 9, fontWeight: 600, letterSpacing: 0.5 }}>RECOMMANDE</div>}
      <h4 style={{ fontSize: 13, fontWeight: 600, color, margin: '0 0 8px' }}>{title}</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 11, color: '#555' }}>
        <div>Agents jour : <EditableVal value={j} color={color} onChange={v => handleChange('agents_jour', v)} /></div>
        <div>Agents nuit : <EditableVal value={n} color={color} onChange={v => handleChange('agents_nuit', v)} /></div>
        <div>Rondes/vacation : <EditableVal value={rondes} color={color} onChange={v => handleChange('rondes', v)} /></div>
        <div>Telesurveillance : {tsv ? 'Oui' : 'Non'}</div>
        <div>Points QR : <EditableVal value={qr} color={color} onChange={v => handleChange('points_qr', v)} /></div>
        <div>Score securite : <span style={{ fontWeight: 400, color }}>{score}/100</span></div>
      </div>
      {equip.length > 0 && (
        <div style={{ marginTop: 4, fontSize: 10, color: '#888', fontWeight: 400 }}>
          Equipements : {equip.join(', ')}
        </div>
      )}
      {warning && zones && zones.length > 0 && (
        <div style={{ marginTop: 6, padding: 6, background: '#FEE2E2', borderRadius: 6, fontSize: 10, color: '#DC2626', fontWeight: 400 }}>
          Zones NON couvertes :
          <ul style={{ margin: '2px 0 0 12px', padding: 0 }}>{zones.map((z, i) => <li key={i}>{z}</li>)}</ul>
        </div>
      )}
      <div style={{ marginTop: 8, padding: '6px 0', borderTop: `1px solid ${color}25`, textAlign: 'center' }}>
        <div style={{ fontSize: 9, fontWeight: 400, color: '#888', textTransform: 'uppercase' }}>Cout base (agents)</div>
        <div style={{ fontSize: 16, fontWeight: 400, color }}>{cout.toLocaleString()} FCFA</div>
      </div>
      {hasTarifs && (
        <div style={{ marginTop: 4 }}>
          <div style={{ fontSize: 9, fontWeight: 400, color: '#888', textTransform: 'uppercase', marginBottom: 4, textAlign: 'center' }}>Tarifs client par offre</div>
          <div style={{ display: 'flex', gap: 3 }}>
            {Object.entries(tarifs).map(([nom, d]) => (
              <div key={nom} style={{
                flex: 1, padding: '4px 2px', borderRadius: 6, textAlign: 'center',
                background: (OFFRE_COLORS[nom] || '#888') + '10', border: `1px solid ${OFFRE_COLORS[nom] || '#888'}30`,
              }}>
                <div style={{ fontSize: 9, fontWeight: 400, color: OFFRE_COLORS[nom] || '#888' }}>{nom}</div>
                <div style={{ fontSize: 11, fontWeight: 400, color: OFFRE_COLORS[nom] || '#888' }}>{Number(d.cout_mensuel || 0).toLocaleString()}</div>
                <div style={{ fontSize: 8, color: '#999' }}>FCFA</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── PARAMS MODAL ──
function ParamsModal({ plan, auditScore, onApply, onClose }) {
  const prefixes = [
    { key: 'sc_a', label: 'Scenario A' },
    { key: 'sc_b', label: 'Scenario B' },
    { key: 'sc_c', label: 'Scenario C' },
  ];
  const [params, setParams] = useState(() => {
    const p = {};
    for (const { key } of prefixes) {
      p[key] = {
        agents_jour: plan[`${key}_agents_jour`] || 0,
        agents_nuit: plan[`${key}_agents_nuit`] || 0,
        tarif_unitaire: SALAIRE,
        rondes: plan[`${key}_rondes`] || 0,
        telesurveillance: !!plan[`${key}_telesurveillance`],
        points_qr: plan[`${key}_points_qr`] || 0,
      };
    }
    return p;
  });

  const calcCout = (s) => {
    const total = (s.agents_jour + s.agents_nuit) * 2;
    return total * s.tarif_unitaire + (s.telesurveillance ? TSV_MENSUEL : 0) + s.points_qr * QR_UNIT;
  };

  const set = (prefix, field, value) => setParams(p => ({ ...p, [prefix]: { ...p[prefix], [field]: value } }));

  const handleApply = () => {
    const updates = {};
    for (const { key } of prefixes) {
      const s = params[key];
      updates[`${key}_agents_jour`] = s.agents_jour;
      updates[`${key}_agents_nuit`] = s.agents_nuit;
      updates[`${key}_rondes`] = s.rondes;
      updates[`${key}_telesurveillance`] = s.telesurveillance;
      updates[`${key}_points_qr`] = s.points_qr;
      updates[`${key}_cout_mensuel`] = calcCout(s);
      updates[`${key}_score_securite`] = recalcScore(auditScore, s.agents_jour, s.agents_nuit);

      const oldTarifs = (() => { try { return typeof plan[`${key}_tarifs`] === 'string' ? JSON.parse(plan[`${key}_tarifs`]) : (plan[`${key}_tarifs`] || {}); } catch { return {}; } })();
      updates[`${key}_tarifs`] = JSON.stringify(recalcTarifs(s.agents_jour, s.agents_nuit, oldTarifs));
    }
    onApply(updates);
  };

  return (
    <div className="gss-modal-overlay" onClick={onClose}>
      <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 800 }}>
        <div className="gss-modal-header">
          <div>
            <h2>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9c.2.65.76 1.1 1.44 1.12H21a2 2 0 010 4h-.09c-.68.02-1.24.47-1.44 1.12z"/></svg>
              Modifier les parametres
            </h2>
            <div className="gss-modal-subtitle">Les couts se recalculent en temps reel</div>
          </div>
          <button className="gss-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div style={{ padding: '1.5rem', overflowY: 'auto', maxHeight: '60vh' }}>
          <div style={{ display: 'flex', gap: 16 }}>
            {prefixes.map(({ key, label }) => {
              const s = params[key];
              const cout = calcCout(s);
              return (
                <div key={key} style={{ flex: 1, padding: 16, borderRadius: 10, background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#1A1A2E' }}>{label}</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#555' }}>Agents jour</label>
                      <input className="gss-input" type="number" min="0" value={s.agents_jour} onChange={e => set(key, 'agents_jour', parseInt(e.target.value) || 0)} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#555' }}>Agents nuit</label>
                      <input className="gss-input" type="number" min="0" value={s.agents_nuit} onChange={e => set(key, 'agents_nuit', parseInt(e.target.value) || 0)} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#555' }}>Tarif unitaire agent (FCFA)</label>
                      <input className="gss-input" type="number" min="0" value={s.tarif_unitaire} onChange={e => set(key, 'tarif_unitaire', parseInt(e.target.value) || 0)} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#555' }}>Rondes par vacation</label>
                      <input className="gss-input" type="number" min="0" value={s.rondes} onChange={e => set(key, 'rondes', parseInt(e.target.value) || 0)} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#555' }}>Telesurveillance</label>
                      <input type="checkbox" checked={s.telesurveillance} onChange={e => set(key, 'telesurveillance', e.target.checked)} style={{ accentColor: '#8B1A1A' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#555' }}>Points QR</label>
                      <input className="gss-input" type="number" min="0" value={s.points_qr} onChange={e => set(key, 'points_qr', parseInt(e.target.value) || 0)} />
                    </div>
                  </div>
                  <div style={{ marginTop: 12, padding: '8px 0', borderTop: '1px solid #E5E7EB', textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>Cout estime</div>
                    <div style={{ fontSize: 18, fontWeight: 900, color: '#1A1A2E' }}>{cout.toLocaleString()} FCFA</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="gss-modal-actions">
          <button className="gss-btn-cancel" onClick={onClose}>Annuler</button>
          <button className="gss-btn-deploy" onClick={handleApply}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
            Appliquer
          </button>
        </div>
      </div>
    </div>
  );
}

// ── DEVIS MODAL ──
function DevisModal({ plan, audit, scenarioChoisi, token, onClose }) {
  const sc = scenarioChoisi || 'a';
  const p = `sc_${sc}`;
  const jour = plan[`${p}_agents_jour`] || 0;
  const nuit = plan[`${p}_agents_nuit`] || 0;
  const totalAgents = (jour + nuit) * 2;

  const [offre, setOffre] = useState('Professionnelle');
  const [sections, setSections] = useState({ video: false, alarmes: false, tsv: false });
  const [items, setItems] = useState(() => {
    const init = {};
    for (const [sec, def] of Object.entries(DEVIS_CATALOG)) {
      init[sec] = def.items.map(i => ({ ...i }));
    }
    return init;
  });
  const [generating, setGenerating] = useState(false);

  const toggleSection = (sec) => setSections(prev => ({ ...prev, [sec]: !prev[sec] }));
  const setQty = (sec, idx, val) => {
    setItems(prev => {
      const copy = { ...prev };
      copy[sec] = [...copy[sec]];
      copy[sec][idx] = { ...copy[sec][idx], qty: Math.max(0, parseInt(val) || 0) };
      return copy;
    });
  };

  const gardiennageMensuel = totalAgents * OFFRE_TARIFS[offre];
  const sectionTotal = (sec) => items[sec].reduce((s, i) => s + i.prix * i.qty, 0);
  const tsvMensuel = sections.tsv ? sectionTotal('tsv') : 0;
  const totalMensuel = gardiennageMensuel + tsvMensuel;
  const totalEquipement = (sections.video ? sectionTotal('video') : 0) + (sections.alarmes ? sectionTotal('alarmes') : 0);
  const totalAnnuel = totalMensuel * 12 + totalEquipement;
  const totalAnnuelRemise = Math.round(totalAnnuel * 0.9);

  const handleGenerate = async () => {
    setGenerating(true);
    const body = {
      scenario: sc,
      offre,
      sections: {
        gardiennage: { totalAgents, tarif: OFFRE_TARIFS[offre], mensuel: gardiennageMensuel, jour, nuit },
        ...(sections.video ? { video: items.video.filter(i => i.qty > 0) } : {}),
        ...(sections.alarmes ? { alarmes: items.alarmes.filter(i => i.qty > 0) } : {}),
        ...(sections.tsv ? { tsv: items.tsv.filter(i => i.qty > 0) } : {}),
      },
      totalMensuel,
      totalEquipement,
      totalAnnuel: totalAnnuelRemise,
    };
    try {
      const res = await fetch(`${API_BASE}/api/audits/${audit.id}/plan/devis-pdf`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`${res.status} ${txt}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) { alert('Erreur devis : ' + (e.message || 'inconnue')); }
    setGenerating(false);
  };

  const SectionToggle = ({ secKey, label }) => (
    <div
      onClick={() => toggleSection(secKey)}
      style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '6px 0' }}
    >
      <input type="checkbox" checked={sections[secKey]} onChange={() => toggleSection(secKey)} style={{ accentColor: '#8B1A1A', width: 16, height: 16 }} />
      <span style={{ fontSize: 13, fontWeight: 600, color: sections[secKey] ? '#1A1A2E' : '#888' }}>{label}</span>
    </div>
  );

  const ItemsTable = ({ secKey }) => (
    <div style={{ marginLeft: 24, marginBottom: 8 }}>
      {items[secKey].map((item, idx) => (
        <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12 }}>
          <span style={{ flex: 1, color: '#555' }}>{item.label}</span>
          <input
            type="number" min="0" value={item.qty}
            onChange={e => setQty(secKey, idx, e.target.value)}
            style={{ width: 50, textAlign: 'center', border: '1px solid #DDD', borderRadius: 4, padding: '2px 4px', fontSize: 12 }}
          />
          <span style={{ width: 80, textAlign: 'right', color: '#888', fontSize: 11 }}>{item.prix.toLocaleString()} F</span>
          <span style={{ width: 90, textAlign: 'right', fontWeight: 600, color: '#1A1A2E' }}>{(item.prix * item.qty).toLocaleString()} F</span>
        </div>
      ))}
      <div style={{ textAlign: 'right', fontWeight: 700, fontSize: 12, color: '#8B1A1A', paddingTop: 4, borderTop: '1px solid #EEE', marginTop: 4 }}>
        Sous-total : {sectionTotal(secKey).toLocaleString()} FCFA{DEVIS_CATALOG[secKey].mensuel ? '/mois' : ''}
      </div>
    </div>
  );

  return (
    <div className="gss-modal-overlay" onClick={onClose}>
      <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
        <div className="gss-modal-header">
          <div>
            <h2>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              Composer le devis client
            </h2>
            <div className="gss-modal-subtitle">Scenario {sc.toUpperCase()} — {audit.client_nom}</div>
          </div>
          <button className="gss-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div style={{ padding: '1rem 1.5rem', overflowY: 'auto', maxHeight: '60vh' }}>
          {/* SECTION A — Gardiennage */}
          <div style={{ padding: '8px 0', borderBottom: '1px solid #EEE' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
              <input type="checkbox" checked disabled style={{ width: 16, height: 16, accentColor: '#8B1A1A' }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1A1A2E' }}>A — Gardiennage</span>
              <span style={{ fontSize: 11, color: '#888', marginLeft: 'auto' }}>Obligatoire</span>
            </div>
            <div style={{ marginLeft: 24, fontSize: 12, color: '#555' }}>
              <div style={{ display: 'flex', gap: 16, marginBottom: 6 }}>
                <span>Agents jour : <strong>{jour}</strong></span>
                <span>Agents nuit : <strong>{nuit}</strong></span>
                <span>Total rotation : <strong>{totalAgents}</strong></span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span>Offre :</span>
                <select value={offre} onChange={e => setOffre(e.target.value)} style={{ border: '1px solid #DDD', borderRadius: 4, padding: '2px 6px', fontSize: 12 }}>
                  {Object.entries(OFFRE_TARIFS).map(([nom, tarif]) => (
                    <option key={nom} value={nom}>{nom} ({tarif.toLocaleString()} F/agent/mois)</option>
                  ))}
                </select>
              </div>
              <div style={{ textAlign: 'right', fontWeight: 700, fontSize: 12, color: '#8B1A1A' }}>
                Sous-total : {gardiennageMensuel.toLocaleString()} FCFA/mois
              </div>
            </div>
          </div>

          {/* SECTION B — Video */}
          <div style={{ padding: '8px 0', borderBottom: '1px solid #EEE' }}>
            <SectionToggle secKey="video" label="B — Videosurveillance" />
            {sections.video && <ItemsTable secKey="video" />}
          </div>

          {/* SECTION C — Alarmes */}
          <div style={{ padding: '8px 0', borderBottom: '1px solid #EEE' }}>
            <SectionToggle secKey="alarmes" label="C — Alarmes & Detection" />
            {sections.alarmes && <ItemsTable secKey="alarmes" />}
          </div>

          {/* SECTION D — TSV */}
          <div style={{ padding: '8px 0', borderBottom: '1px solid #EEE' }}>
            <SectionToggle secKey="tsv" label="D — Telesurveillance" />
            {sections.tsv && <ItemsTable secKey="tsv" />}
          </div>

          {/* TOTAUX */}
          <div style={{ marginTop: 12, padding: 12, background: '#F9FAFB', borderRadius: 8, fontSize: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: '#555' }}>
              <span>Gardiennage</span><span>{gardiennageMensuel.toLocaleString()} FCFA/mois</span>
            </div>
            {sections.tsv && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: '#555' }}>
              <span>Telesurveillance</span><span>{tsvMensuel.toLocaleString()} FCFA/mois</span>
            </div>}
            {sections.video && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: '#555' }}>
              <span>Videosurveillance (equipement)</span><span>{sectionTotal('video').toLocaleString()} FCFA</span>
            </div>}
            {sections.alarmes && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: '#555' }}>
              <span>Alarmes & Detection (equipement)</span><span>{sectionTotal('alarmes').toLocaleString()} FCFA</span>
            </div>}
            <div style={{ borderTop: '2px solid #8B1A1A', marginTop: 6, paddingTop: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: '#1A1A2E' }}>
                <span>TOTAL MENSUEL</span><span>{totalMensuel.toLocaleString()} FCFA</span>
              </div>
              {totalEquipement > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, color: '#555', fontSize: 11 }}>
                <span>+ Equipement (ponctuel)</span><span>{totalEquipement.toLocaleString()} FCFA</span>
              </div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, color: '#8B1A1A', fontSize: 14, marginTop: 4 }}>
                <span>TOTAL ANNUEL (-10%)</span><span>{totalAnnuelRemise.toLocaleString()} FCFA</span>
              </div>
            </div>
          </div>
        </div>
        <div className="gss-modal-actions">
          <button className="gss-btn-cancel" onClick={onClose}>Annuler</button>
          <button className="gss-btn-deploy" onClick={handleGenerate} disabled={generating}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            {generating ? 'Generation...' : 'Generer le devis PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── MAIN PAGE ──
export default function PlanPrevention() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showParams, setShowParams] = useState(false);
  const [scenarioChoisi, setScenarioChoisi] = useState('');
  const [saving, setSaving] = useState(false);
  const [showDevis, setShowDevis] = useState(false);

  useEscapeClose(showParams, () => setShowParams(false));
  useEscapeClose(showDevis, () => setShowDevis(false));

  const load = useCallback(async () => {
    try {
      const [aRes, pRes] = await Promise.all([
        api.get(`/audits/${id}`),
        api.get(`/audits/${id}/plan`).catch(() => null),
      ]);
      setAudit(aRes.data);
      if (pRes) {
        setPlan(pRes.data);
        setScenarioChoisi(pRes.data.scenario_choisi || '');
      }
    } catch {}
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    try {
      const res = await api.post(`/audits/${id}/plan`);
      setPlan(res.data);
    } catch {}
  };

  const savePlan = async (updates) => {
    setSaving(true);
    try {
      const res = await api.put(`/audits/${id}/plan`, updates);
      setPlan(res.data);
    } catch {}
    setSaving(false);
  };

  const handleFieldChange = (updates) => {
    setPlan(prev => ({ ...prev, ...updates }));
    savePlan(updates);
  };

  const handleParamsApply = (updates) => {
    setShowParams(false);
    setPlan(prev => ({ ...prev, ...updates }));
    savePlan(updates);
  };

  const handleScenarioChoisi = async (sc) => {
    setScenarioChoisi(sc);
    await savePlan({ scenario_choisi: sc });

    // Auto-generate decharge for B or C
    if (sc === 'b' || sc === 'c') {
      const zones = (() => { try { return JSON.parse(plan?.sc_c_zones_non_couvertes || '[]'); } catch { return []; } })();
      try {
        await api.post('/audits/decharges', {
          audit_id: id,
          plan_id: plan?.id,
          client_nom: audit?.client_nom || '',
          signataire_nom: '',
          signataire_fonction: '',
          date_signature: aujourdHui(),
          notes: `Scenario ${sc.toUpperCase()} choisi. Zones non couvertes : ${zones.join(', ') || 'Aucune'}`,
        });
      } catch {}
    }
  };

  const printPlan = async () => {
    try {
      const res = await api.get(`/audits/${id}/plan/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch {}
  };

  const generateDevis = () => setShowDevis(true);

  if (loading) return <p style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>Chargement...</p>;
  if (!audit) return <p style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>Audit non trouve</p>;

  const zones = (() => { try { return JSON.parse(plan?.sc_c_zones_non_couvertes || '[]'); } catch { return []; } })();

  return (
    <div style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1A1A2E', margin: 0 }}>Plan de prevention — {audit.numero}</h3>
          <p style={{ fontSize: 12, color: '#888', margin: '2px 0 0', fontWeight: 400 }}>{audit.client_nom} — Score: {audit.score_total}/100</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!plan && <button className="gss-btn-deploy" onClick={generate}>Generer le plan</button>}
          <button className="gss-btn-cancel" onClick={() => navigate('/audit/liste')}>Retour</button>
        </div>
      </div>

      {plan ? (
        <>
          {saving && <div style={{ textAlign: 'center', padding: 2, fontSize: 10, color: '#888', fontWeight: 400 }}>Sauvegarde...</div>}

          <div style={{ display: 'flex', gap: 10, width: '100%' }}>
            <ScenarioCard title="SCENARIO A — Recommande" color="#2e7d32" bg="#F0FDF4" data={plan} prefix="sc_a" recommended onFieldChange={handleFieldChange} auditScore={audit.score_total} />
            <ScenarioCard title="SCENARIO B — Intermediaire" color="#1565c0" bg="#EFF6FF" data={plan} prefix="sc_b" onFieldChange={handleFieldChange} auditScore={audit.score_total} />
            <ScenarioCard title="SCENARIO C — Minimum" color="#DC2626" bg="#FEF2F2" data={plan} prefix="sc_c" warning zones={zones} onFieldChange={handleFieldChange} auditScore={audit.score_total} />
          </div>

          {/* ACTION BUTTONS */}
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <button className="gss-btn" style={{ background: '#8B1A1A', color: '#fff', fontWeight: 500, fontSize: 12, padding: '7px 14px', borderRadius: 6 }} onClick={printPlan}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 4, verticalAlign: 'middle' }}><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
              Imprimer le plan
            </button>
            <button className="gss-btn" style={{ background: '#1565c0', color: '#fff', fontWeight: 500, fontSize: 12, padding: '7px 14px', borderRadius: 6 }} onClick={() => setShowParams(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 4, verticalAlign: 'middle' }}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4"/></svg>
              Modifier les parametres
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <label style={{ fontSize: 12, fontWeight: 400, color: '#555' }}>Scenario choisi :</label>
              <select
                className="gss-input"
                value={scenarioChoisi}
                onChange={e => handleScenarioChoisi(e.target.value)}
                style={{ width: 180 }}
              >
                <option value="">-- Non defini --</option>
                <option value="a">A — Recommande</option>
                <option value="b">B — Intermediaire</option>
                <option value="c">C — Minimum</option>
              </select>
              {(scenarioChoisi === 'b' || scenarioChoisi === 'c') && (
                <span style={{ fontSize: 10, color: '#DC2626', fontWeight: 400 }}>Decharge generee</span>
              )}
            </div>
            <button
              className="gss-btn"
              style={{ background: '#2e7d32', color: '#fff', fontWeight: 500, fontSize: 12, padding: '7px 14px', borderRadius: 6 }}
              onClick={generateDevis}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 4, verticalAlign: 'middle' }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
              Generer le devis
            </button>
          </div>
          <div style={{ marginTop: 12 }}>
            <IAAnalysisPanel type="audit" buttonLabel="Recommandations IA" data={{
              score_vulnerabilite: audit.score_total,
              zones: zones,
              scenario_choisi: scenarioChoisi,
              site: audit.site_nom || audit.nom_site,
              type_site: audit.type_site,
            }} />
          </div>
        </>
      ) : (
        <div className="content-card" style={{ padding: 40, textAlign: 'center' }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DDD" strokeWidth="1.5" style={{ marginBottom: 16 }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          <p style={{ color: '#666' }}>Aucun plan genere. Cliquez sur "Generer le plan" pour creer les scenarios.</p>
        </div>
      )}

      {showParams && plan && (
        <ParamsModal plan={plan} auditScore={audit.score_total} onApply={handleParamsApply} onClose={() => setShowParams(false)} />
      )}
      {showDevis && plan && (
        <DevisModal plan={plan} audit={audit} scenarioChoisi={scenarioChoisi} token={token} onClose={() => setShowDevis(false)} />
      )}
    </div>
  );
}
